/**
 * What one submitted value means as a review, and what the stored field looks
 * like the moment it is written.
 *
 * Pure on purpose: the queue does the network and the rollback, the editor
 * does the rendering, and both ask this file the same question so they cannot
 * answer it differently. It is also where the wording of the three acts
 * lives, in one place, so no screen has to invent its own.
 */

import type { DocumentResultField, FieldReview } from '@/types/domain'
import { resultFieldValue } from '@/types/domain'

/**
 * `accept` is the single most common action in the product: the reviewer
 * signs off on what was read. It is not "nothing happened" — the gateway
 * records the review and copies the **delivered** value into `finalText`,
 * which is what takes the field out of the queue.
 *
 * `correct` carries whatever the reviewer put in, **including the empty
 * string**: now that the gateway tells "no correction given" from "corrected
 * to nothing", an emptied field is an ordinary correction storing an empty
 * `finalText` — the stored representation of "a reviewer says this field is
 * blank" that `resultFieldValue` has always honoured.
 */
export type ReviewSubmission =
  | { kind: 'accept' }
  | { kind: 'correct'; correction: string }

/**
 * Turns "the value in the box when Enter was pressed" into a review.
 *
 * Unchanged is an acceptance — including an unchanged *empty* value, which is
 * how a genuinely blank field gets confirmed. Emptying a field that held
 * something is a correction to `''`: a different act, stored differently, and
 * the screens say so in words before it is written.
 */
export function reviewSubmission(current: string, next: string): ReviewSubmission {
  if (next === current) return { kind: 'accept' }
  // Whitespace alone is nobody's intended value; it is somebody having
  // emptied the box. Normalising it here means the row and the panel cannot
  // disagree about whether "   " clears a field, and the gateway stores the
  // same empty finalText either way.
  if (next.trim() === '') return { kind: 'correct', correction: '' }
  return { kind: 'correct', correction: next }
}

/**
 * Whether this submission will deliver an empty value where something was
 * read — the one act easily mistaken for confirming a blank, and the one the
 * screens therefore have to name differently.
 */
export function clearsValue(submission: ReviewSubmission): boolean {
  return submission.kind === 'correct' && submission.correction === ''
}

/**
 * The label of the button that commits `submission`, so the per-document
 * panel and the bulk row cannot describe the same act in two ways: sign off
 * on what stands, replace it, or record that the field is empty.
 */
export function submitLabel(submission: ReviewSubmission): string {
  if (clearsValue(submission)) return 'Als leer speichern'
  return submission.kind === 'correct' ? 'Korrektur speichern' : 'Übernehmen'
}

/** The value that will stand once `submission` is written. */
export function finalTextAfter(field: DocumentResultField, submission: ReviewSubmission): string {
  if (submission.kind === 'correct') return submission.correction
  // An acceptance confirms the DELIVERED value, not the recognizer's text:
  // the gateway writes back whatever the field currently delivers, so
  // accepting an already corrected field no longer reverts it.
  // `resultFieldValue` is the very precedence rule the gateway applies.
  return resultFieldValue(field)
}

/** Who a review is recorded as, when the client fills one in optimistically. */
export interface Reviewer {
  id?: string
  name?: string
}

/**
 * The field as it will look once the write lands — rendered before the
 * request returns so the screen never stalls on the network, and thrown away
 * again if the write fails.
 *
 * The review it appends is the shape the *stored* result uses (camelCase),
 * not the 201 receipt's (snake_case): this field goes back into
 * `EvaluationDocument.result`, where everything else reads it.
 */
export function optimisticField(
  field: DocumentResultField,
  submission: ReviewSubmission,
  reviewer: Reviewer = {},
): DocumentResultField {
  const review: FieldReview = {
    accepted: submission.kind === 'accept',
    correction: submission.kind === 'correct' ? submission.correction : '',
    reviewedBy: reviewer.id ?? '',
    reviewedByName: reviewer.name,
    reviewedAt: new Date().toISOString(),
  }
  return {
    ...field,
    finalText: finalTextAfter(field, submission),
    reviews: [...(field.reviews ?? []), review],
  }
}

/** What the server says stands, folded back onto the field once the receipt
 *  is in. Only `finalText` and the last review can differ from the optimistic
 *  guess (the reviewer's resolved name, the server's clock), and letting the
 *  server win keeps the screen and the store identical. */
export function appliedField(
  field: DocumentResultField,
  optimistic: DocumentResultField,
  receipt: { accepted: boolean; correction: string; finalText: string; reviewedBy: string; reviewedByName?: string; reviewedAt?: string },
): DocumentResultField {
  const review: FieldReview = {
    accepted: receipt.accepted,
    correction: receipt.correction,
    reviewedBy: receipt.reviewedBy,
    reviewedByName: receipt.reviewedByName,
    reviewedAt: receipt.reviewedAt,
  }
  return {
    ...optimistic,
    finalText: receipt.finalText,
    reviews: [...(field.reviews ?? []), review],
  }
}

/** The delivered value of a field, re-exported so a review screen never
 *  reaches for `finalText` or `text` itself. */
export { resultFieldValue }
